import { useQuery } from "@tanstack/react-query";
import type { HistoricoPagamentosContrato } from "@atlas/sdk";
import { Button, Pill, fmtBRL } from "@atlas/ui/web";
import { Modal } from "../routes/prefeitura/_ui";
import { rotuloRegistro } from "../lib/registro-agente";

type Props = {
  adf: string;
  carregar: () => Promise<HistoricoPagamentosContrato>;
  onClose: () => void;
  competenciaDestaque?: string;
  operacional?: boolean;
};

const RESULTADOS: Record<string, { label: string; variant: "averbado" | "pendente" | "expirado" | "aceita" }> = {
  aplicada: { label: "Integral", variant: "averbado" },
  parcial: { label: "Parcial", variant: "pendente" },
  falha: { label: "Não descontado", variant: "expirado" },
  interrompida_desligamento: { label: "Interrompido", variant: "expirado" },
  suspensa_sem_folha: { label: "Sem folha no mês", variant: "pendente" },
  suspensa_por_teto: { label: "Suspenso pelo teto", variant: "pendente" },
  recebida: { label: "Recebido", variant: "aceita" },
};

function fmtCompetencia(valor: string): string {
  return /^\d{6}$/.test(valor) ? `${valor.slice(4, 6)}/${valor.slice(0, 4)}` : valor;
}

function produtoLabel(valor: string): string {
  const labels: Record<string, string> = {
    EMPRESTIMO: "Empréstimo consignado",
    CARTAO_CONSIGNADO: "Cartão consignado",
    CARTAO_BENEFICIO: "Cartão benefício",
    CARTAO_BENEFICIOS: "Cartão benefício",
    TELEMEDICINA: "Plano de telemedicina",
    PORTABILIDADE: "Portabilidade",
    REFIN: "Portabilidade",
  };
  return labels[valor] ?? valor;
}

function Resumo({ label, value, destaque }: { label: string; value: string; destaque?: boolean }) {
  return (
    <div style={{ padding: 12, borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-elev-2)" }}>
      <div style={{ color: "var(--text-dim)", fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 4, fontSize: 19, fontWeight: 700, color: destaque ? "var(--gold-500)" : "var(--text)" }}>{value}</div>
    </div>
  );
}

export function HistoricoPagamentosContratoModal({
  adf,
  carregar,
  onClose,
  competenciaDestaque,
  operacional = false,
}: Props) {
  const q = useQuery({
    queryKey: ["historico-pagamentos-contrato", adf, operacional],
    queryFn: carregar,
    staleTime: 15_000,
  });
  const dados = q.data;
  const fatos = [...(dados?.historico ?? [])].sort((a, b) => b.competencia.localeCompare(a.competencia));

  return (
    <Modal title="Detalhes dos pagamentos" onClose={onClose} maxWidth={820}>
      {q.isLoading ? (
        <div style={{ padding: 28, textAlign: "center", color: "var(--text-muted)" }}>Carregando histórico…</div>
      ) : q.isError || !dados ? (
        <div style={{ display: "grid", justifyItems: "center", gap: 12, padding: 24, color: "var(--text-muted)" }}>
          <span>Não foi possível carregar os pagamentos deste contrato.</span>
          <Button size="sm" variant="ghost" onClick={() => q.refetch()}>Tentar novamente</Button>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 700 }}>{dados.contrato.nome}</div>
              <div style={{ marginTop: 3, color: "var(--text-muted)", fontSize: 12 }}>
                ADF <span style={{ fontFamily: "var(--font-mono)" }}>{dados.contrato.adf}</span> · matrícula{" "}
                <span style={{ fontFamily: "var(--font-mono)" }}>{dados.contrato.matricula}</span>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 12, color: "var(--text-muted)" }}>
              <div>{dados.contrato.credor}</div>
              <div>{produtoLabel(dados.contrato.produto)}</div>
              {/* So' a visao da averbadora traz o campo (`undefined` nas demais):
                  quem registrou pelo banco e de qual sub organizacao. */}
              {dados.contrato.registradoPor !== undefined ? (
                <div title="Quem registrou o contrato pela instituição">Registrado por: {rotuloRegistro(dados.contrato.registradoPor)}</div>
              ) : null}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 8 }}>
            <Resumo label="Parcelas quitadas" value={dados.contrato.cronogramaPendente ? "Cronograma pendente" : `${dados.contrato.parcelasPagas}/${dados.contrato.totalParcelas}`} />
            <Resumo label="Parcelas restantes" value={dados.contrato.parcelasRestantes == null ? "Não informado" : String(dados.contrato.parcelasRestantes)} destaque={(dados.contrato.parcelasRestantes ?? 0) > 0} />
            <Resumo label="Valor da parcela" value={fmtBRL(dados.contrato.valorParcela)} />
            <Resumo label="Pagamentos parciais" value={String(dados.contrato.pagamentosParciais)} destaque={dados.contrato.pagamentosParciais > 0} />
          </div>

          {dados.contrato.historicoAnteriorConhecido && (dados.contrato.parcelasAnterioresSemDetalhamento ?? 0) > 0 ? (
            <div style={{ padding: "10px 12px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--bg-elev-2)", color: "var(--text-muted)", fontSize: 12 }}>
              <b style={{ color: "var(--text)" }}>{dados.contrato.parcelasAnterioresSemDetalhamento} parcela(s)</b> já estavam pagas quando o contrato entrou na ATLAS. Os valores individuais anteriores não foram informados.
            </div>
          ) : !dados.contrato.historicoAnteriorConhecido && dados.contrato.parcelasPagas > 0 ? (
            <div style={{ padding: "10px 12px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--bg-elev-2)", color: "var(--text-muted)", fontSize: 12 }}>
              Este contrato possui parcelas pagas anteriores ao início deste histórico. A ATLAS preserva a quantidade total, mas não possui os valores individuais desse período.
            </div>
          ) : null}

          <div>
            <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Pagamentos por competência</h4>
            {fatos.length === 0 ? (
              <div style={{ padding: 22, borderRadius: 10, border: "1px dashed var(--border-strong)", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                Nenhum pagamento mensal foi registrado para este contrato ainda.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 7 }}>
                {fatos.map((fato) => {
                  const status = RESULTADOS[fato.resultado] ?? { label: fato.resultado, variant: "pendente" as const };
                  const presumido = fato.origem === "presumido";
                  const destaque = competenciaDestaque === fato.competencia;
                  return (
                    <div key={fato.id} style={{ padding: 12, borderRadius: 10, border: `1px solid ${destaque ? "var(--accent)" : fato.resultado === "parcial" ? "var(--gold-500)" : "var(--border)"}`, background: destaque ? "color-mix(in srgb, var(--accent) 8%, var(--bg-elev-2))" : "var(--bg-elev-2)", display: "grid", gap: 7 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <strong>{fmtCompetencia(fato.competencia)}{destaque ? " · competência selecionada" : ""}</strong>
                        <div style={{ display: "flex", gap: 6 }}>
                          {presumido ? <Pill variant="pendente">Presumido</Pill> : null}
                          <Pill variant={status.variant}>{status.label}</Pill>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))", gap: 8, fontSize: 12 }}>
                        <span style={{ color: "var(--text-muted)" }}>Previsto<br /><b style={{ color: "var(--text)" }}>{fmtBRL(fato.valorInstruido)}</b></span>
                        {/* Verde só quando ENTROU dinheiro, na mesma régua do
                            "Não descontado" logo abaixo: zero pago em verde
                            leria como recebido. `--success` e não `--emerald-*`
                            cru porque ele troca de tom no tema claro, onde o 500
                            perde contraste sobre fundo claro. */}
                        <span style={{ color: "var(--text-muted)" }}>Pago<br /><b style={{ color: fato.valorEfetivo > 0 ? "var(--success)" : "var(--text)" }}>{fmtBRL(fato.valorEfetivo)}</b></span>
                        {/* "Restante da parcela" ate 15/09/2026 — o rotulo dizia
                            que a parcela seguia devendo, e ela nao segue: parcial
                            encerra a parcela. O que este numero informa e' o que a
                            folha deixou de descontar, cobrado pelo banco por fora. */}
                        <span style={{ color: "var(--text-muted)" }} title="Valor que a folha deixou de descontar. A parcela do mês está encerrada e a próxima segue pelo valor do contrato.">Não descontado<br /><b style={{ color: fato.residuo > 0 ? "var(--gold-500)" : "var(--text)" }}>{fmtBRL(fato.residuo)}</b></span>
                      </div>
                      {fato.motivo ?<div style={{ fontSize: 11, color: "var(--text-muted)" }}>{fato.motivo}</div> : null}
                      {operacional ? (
                        <div style={{ fontSize: 10.5, color: "var(--text-dim)" }}>
                          {fato.origem === "retorno" ? "Retorno da folha" : fato.origem === "manual" ? "Registro manual" : "Fechamento presumido"}
                          {fato.distribuicao ? ` · distribuição ${fato.distribuicao}` : ""}
                          {fato.parte ? ` · parte ${fato.parte}` : ""}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {/* O `Modal` de `prefeitura/_ui` fecha só por clique no scrim — sem
          controle visível. Passava na tela de contratos, onde a grade continua
          atrás do scrim e o caminho de volta é óbvio; não passa quando o modal
          é aberto da ficha do servidor, que sai da tela enquanto ele está aqui. */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose}>Fechar</Button>
      </div>
    </Modal>
  );
}
